import { useAuth } from '@platform/auth-client';
import { Badge, Button, ConfirmDialog, formatNumber, IconButton, type Column } from '@platform/design-system';
import type { TypedClient } from '@platform/openapi';
import { Archive, Pencil, Plus, Warehouse } from 'lucide-react';
import { useState } from 'react';
import { useStockOverview } from '../dashboard/stock.ts';
import { useTenantDetail } from '../catalog/useCatalogQueries.ts';
import { ResourceTable, type ResourceQuery } from '../ResourceTable.tsx';
import { usePageContext } from '../usePageContext.ts';
import { DepotSheet, type DepotDetails } from './DepotSheet.tsx';
import { depotPlaceOf, useDepotChanges } from './depots.ts';
import { INVENTORY_WRITE_ROLE, type Location } from './inventory.ts';
import styles from './Inventory.module.css';

type Editing = { readonly depot: Location | null };

export type DepotsTabProps = {
  api: TypedClient;
  query: ResourceQuery;
  depots: readonly Location[];
  onShowMovements: (depot: Location) => void;
};

export const DepotsTab = ({ api, query, depots, onShowMovements }: DepotsTabProps) => {
  const { t, locale, describeError } = usePageContext();
  const canWrite = useAuth().hasRole(INVENTORY_WRITE_ROLE);
  const figures = new Map(
    (useStockOverview(api, null).data?.depots ?? []).map((depot) => [depot.locationId, depot]),
  );
  const currency = useTenantDetail(api).data?.currency;
  const changes = useDepotChanges(api);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [archiving, setArchiving] = useState<Location | null>(null);
  const refusal = changes.isError && editing === null ? (describeError(changes.error) ?? null) : null;

  const edit = (depot: Location | null) => {
    changes.reset();
    setEditing({ depot });
  };
  const detailsOf = (depot: Location | null): DepotDetails | null =>
    depot === null
      ? null
      : {
          figures: figures.get(depot.id),
          currency,
          onShowMovements: () => {
            onShowMovements(depot);
          },
        };
  const editable = (depot: Location) => canWrite && depot.status === 'active';

  const columns: readonly Column<Location>[] = [
    {
      id: 'name',
      header: t('inventory.depotName'),
      primary: true,
      cell: (depot) => (
        <span className={styles['depotName']}>
          <span className={styles['depotTile']} aria-hidden="true">
            <Warehouse size={14} />
          </span>
          <span className={styles['twoLines']}>
            <strong>{depot.name}</strong>
            {depotPlaceOf(depot) === '' ? null : <span>{depotPlaceOf(depot)}</span>}
          </span>
        </span>
      ),
    },
    {
      id: 'code',
      header: t('inventory.depotCode'),
      cell: (depot) => <span className={styles['muted']}>{depot.code}</span>,
    },
    {
      id: 'phone',
      header: t('depots.phone'),
      cell: (depot) => (depot.phone === null ? '—' : <span dir="ltr">{depot.phone}</span>),
    },
    {
      id: 'available',
      header: t('inventory.unitsAvailable'),
      numeric: true,
      cell: (depot) => <strong>{formatNumber(figures.get(depot.id)?.available ?? 0, locale)}</strong>,
    },
    {
      id: 'low',
      header: t('inventory.depotLow'),
      numeric: true,
      cell: (depot) => formatNumber(figures.get(depot.id)?.low ?? 0, locale),
    },
    {
      id: 'status',
      status: true,
      header: t('catalog.status'),
      cell: (depot) => (
        <Badge tone={depot.status === 'active' ? 'success' : 'neutral'}>
          {t(`catalog.status_${depot.status}`)}
        </Badge>
      ),
    },
    {
      id: 'default',
      header: t('inventory.defaultDepot'),
      cell: (depot) => (depot.isDefault ? t('inventory.yes') : '—'),
    },
    {
      id: 'actions',
      header: t('catalog.actions'),
      action: true,
      cell: (depot) =>
        editable(depot) ? (
          <span className={styles['rowActions']}>
            <IconButton
              size="sm"
              label={t('depots.editRow', { name: depot.name })}
              icon={<Pencil size={16} aria-hidden="true" />}
              onClick={() => {
                edit(depot);
              }}
            />
            {depot.isDefault ? null : (
              <IconButton
                size="sm"
                label={t('depots.archiveRow', { name: depot.name })}
                icon={<Archive size={16} aria-hidden="true" />}
                onClick={() => {
                  changes.reset();
                  setArchiving(depot);
                }}
              />
            )}
          </span>
        ) : null,
    },
  ];

  return (
    <>
      {refusal === null ? null : (
        <p role="alert" data-testid="depot-refusal" className={styles['refusal']}>
          {refusal}
        </p>
      )}
      <ResourceTable
        query={query}
        columns={columns}
        rows={depots}
        rowKey={(depot) => depot.id}
        bare
        onRowClick={(depot) => {
          if (editable(depot)) edit(depot);
        }}
        toolbar={
          canWrite ? (
            <div className={styles['tabTools']}>
              <span className={styles['muted']}>{t('depots.count', { count: depots.length })}</span>
              <Button size="sm" icon={<Plus size={16} aria-hidden="true" />} onClick={() => edit(null)}>
                {t('depots.add')}
              </Button>
            </div>
          ) : undefined
        }
        emptyTitle={t('inventory.noDepots')}
        emptyDescription={t('inventory.noDepotsHelp')}
      />
      {editing === null ? null : (
        <DepotSheet
          api={api}
          depot={editing.depot}
          details={detailsOf(editing.depot)}
          submitting={changes.isPending}
          errorMessage={changes.isError ? (describeError(changes.error) ?? null) : null}
          onClose={() => {
            changes.reset();
            setEditing(null);
          }}
          onSubmit={(body) => {
            changes.mutate(
              { kind: 'save', depot: editing.depot, body },
              { onSuccess: () => setEditing(null) },
            );
          }}
        />
      )}
      {archiving === null ? null : (
        <ConfirmDialog
          title={t('depots.archiveTitle', { name: archiving.name })}
          message={t('depots.archiveMessage')}
          confirmLabel={t('depots.archive')}
          cancelLabel={t('common.cancel')}
          closeLabel={t('common.close')}
          onCancel={() => {
            setArchiving(null);
          }}
          onConfirm={() => {
            changes.mutate({ kind: 'archive', depot: archiving });
            setArchiving(null);
          }}
        />
      )}
    </>
  );
};
